import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Check, Loader2 } from "lucide-react";

const PLAN_LABELS = { plus: "THE CHEAPER WAY PLUS", annual: "THE CHEAPER WAY ANNUAL", lifetime: "FOUNDING LIFETIME" };

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser, user } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setState("timeout"); return; }
    let tries = 0;
    let done = false;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          done = true;
          await refreshUser();
          setState("paid");
          return;
        }
      } catch (e) { /* keep polling */ }
      if (tries >= 8) { setState("timeout"); return; }
      if (!done) setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]); // eslint-disable-line

  const plan = user?.plan;

  return (
    <div className="max-w-3xl mx-auto px-6 py-32 min-h-[70vh] flex flex-col justify-center">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="w-8 h-8 text-[#EF3340] animate-spin mb-6" />
          <h1 className="font-display text-4xl md:text-6xl leading-[0.9] mb-4">CONFIRMING…</h1>
          <p className="font-serif text-xl text-stone-300">Hang tight while we confirm your payment.</p>
        </div>
      )}
      {state === "paid" && (
        <>
          <div className="w-12 h-12 border-2 border-[#5FB878] flex items-center justify-center mb-6"><Check className="w-6 h-6 text-[#5FB878]" /></div>
          <div className="eyebrow mb-3 text-[#5FB878]">PAYMENT CONFIRMED</div>
          <h1 data-testid="payment-success" className="font-display text-4xl md:text-7xl leading-[0.9] mb-5">YOU'RE IN.</h1>
          <p className="font-serif text-xl text-stone-300 max-w-lg mb-8">
            {plan ? `You're on ${PLAN_LABELS[plan] || "Pro"}. ` : ""}Unlimited audits, saved results, your personalized feed, and Pro-only finds are unlocked.
          </p>
          <Link to="/find" data-testid="success-go-find" className="inline-flex items-center gap-2 font-label text-xs tracking-widest bg-[#EF3340] text-white px-6 py-4 self-start hover:bg-[#cc2a35] transition-colors">
            START AUDITING <ArrowRight className="w-3 h-3" />
          </Link>
        </>
      )}
      {state === "timeout" && (
        <div data-testid="payment-timeout">
          <div className="eyebrow mb-3 text-[#D4AF37]">STILL PROCESSING</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[0.9] mb-4">ALMOST THERE.</h1>
          <p className="font-serif text-xl text-stone-300 max-w-lg mb-8">Your payment is still confirming. It can take a moment — refresh your account shortly, or check the pricing page.</p>
          <Link to="/pro" className="inline-flex items-center gap-2 font-label text-xs tracking-widest border hairline px-6 py-4 self-start hover:border-[#EF3340]">BACK TO PRICING</Link>
        </div>
      )}
    </div>
  );
}
